// @LTG-ID: F-014
// @STATUS: BUILT
// @HERMETIC: false  // consumes useSector, getSector, SAMPLE_REPORT
// @DOCS: 08_FEATURES.md#F-014, source Landing Page.html lines 2264-2342
// @CHANGED: 2026-02 session_003 (CHG-0043)
// @OWNER: web
//
// F-014 — Three-page faux PDF preview (cover / summary / signature). The
// business NAME on the cover re-skins from `useSector()`; defaults to "Your
// business" when no sector is selected. All other numbers are static defaults
// from `data/sample-report.js`. Sara's signature uses italic Instrument Serif.

import { getSector } from "../../data/sectors";
import { useSector } from "../../lib/use-sector";
import {
    SAMPLE_REPORT,
    DEFAULT_BUSINESS_NAME,
} from "../../data/sample-report";
import BubblePeriod from "../brand/BubblePeriod";
import ItalicPeriod from "../brand/ItalicPeriod";
import "./sample-report.css";

export function SampleReport() {
    const { sector } = useSector();
    const sectorObj = sector ? getSector(sector) : null;
    const businessName = sectorObj?.example || DEFAULT_BUSINESS_NAME;
    const sr = SAMPLE_REPORT;

    return (
        <section
            className="ltg-sample-section"
            id="sample"
            data-testid="sample-report-section"
        >
            <div className="ltg-container">
                <div className="ltg-block-head">
                    <span
                        className="ltg-eyebrow"
                        data-testid="sample-report-eyebrow"
                    >
                        What you get
                    </span>
                    <h2 data-testid="sample-report-h2">
                        A complete report, signed by a CPA
                        <ItalicPeriod /> Yours to keep.
                    </h2>
                </div>

                <div
                    className="ltg-sample-spread"
                    data-testid="sample-report-spread"
                >
                    {/* Cover page */}
                    <div className="ltg-sample-page ltg-sample-page--cover">
                        <div className="ltg-sample-page__corner">
                            KGOB · Indicative Valuation Report
                        </div>
                        <div className="ltg-sample-page__body">
                            <span className="ltg-sample-page__eyebrow">
                                Strategic Snapshot
                            </span>
                            <h3
                                className="ltg-sample-page__h"
                                data-testid="sample-report-business-name"
                            >
                                {businessName}
                                <BubblePeriod />
                            </h3>
                            <div className="ltg-sample-page__meta">
                                {sr.pageMeta.methodologyLine}
                                <br />
                                {sr.pageMeta.signatureLine}
                            </div>
                        </div>
                        <div className="ltg-sample-page__foot">
                            PAGE 01 · COVER
                        </div>
                    </div>

                    {/* Summary page */}
                    <div className="ltg-sample-page ltg-sample-page--summary">
                        <div className="ltg-sample-page__corner">
                            Executive Summary
                        </div>
                        <div className="ltg-sample-page__body">
                            <div className="ltg-sum-row">
                                <span className="ltg-sum-k">
                                    Indicative value
                                </span>
                                <span className="ltg-sum-v">
                                    {sr.indicativeValue}
                                </span>
                            </div>
                            {sr.scores.map((s) => (
                                <div className="ltg-sum-row" key={s.label}>
                                    <span className="ltg-sum-k">{s.label}</span>
                                    <span className="ltg-sum-v">
                                        {s.value} / 100
                                    </span>
                                </div>
                            ))}
                            <div className="ltg-sum-bar">
                                <div
                                    className="ltg-sum-bar__fill"
                                    style={{
                                        width: `${sr.progressFillPct}%`,
                                    }}
                                />
                            </div>
                            <div className="ltg-sum-priorities">
                                <span className="ltg-sum-priorities__k">
                                    Top three priority moves
                                </span>
                                <ol>
                                    {sr.priorityMoves.map((m) => (
                                        <li key={m}>{m}</li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                        <div className="ltg-sample-page__foot">
                            PAGE 04 · EXECUTIVE SUMMARY
                        </div>
                    </div>

                    {/* Signature page */}
                    <div className="ltg-sample-page ltg-sample-page--signature">
                        <div className="ltg-sample-page__corner">
                            Methodology &amp; Signature
                        </div>
                        <div className="ltg-sample-page__body">
                            <span className="ltg-sample-page__eyebrow">
                                Reviewed and signed
                            </span>
                            <div className="ltg-sig-mark">
                                <span
                                    className="ltg-sig-script"
                                    data-testid="sample-report-signature"
                                >
                                    {sr.signature.scriptName}
                                </span>
                                <span className="ltg-sig-rule" />
                                <div className="ltg-sig-meta">
                                    {sr.signature.metaLines.map((line, i) => (
                                        <span key={i}>
                                            {line}
                                            <br />
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <p className="ltg-sig-disclaimer">
                                {sr.signature.disclaimer}
                            </p>
                        </div>
                        <div className="ltg-sample-page__foot">
                            FINAL PAGE · SIGNATURE
                        </div>
                    </div>
                </div>

                <p className="ltg-sample-fine">{sr.fineprint}</p>
            </div>
        </section>
    );
}

export default SampleReport;
